<template>
    <!-- TODO check sidebar in review mode -->
    <div v-if="isReviewModeActive && $reviewStore.review" class="sidebar-edit-fabs">
        <FabWrapper
            :invertedIndex="10"
            :hovering="hoverKey === 'portfolio-hero'"
            :section="section"
        />
    </div>

    <SectionBackground
        :section="section"
    />

    <div ref="content" class="ufo-sec ufo-sec--hero" :style="contentStyle">
        <PortfolioNavigationLogo
            v-if="showLogo"
            :logo="logo"
            :user-name="userName"
            @navigate-home="navigateHome"
        />

        <PortfolioNavigationBar
            v-if="showNavigationAbove"
            :navigation="$store.state.portfolio.portfolioNavigation"
        />

        <div class="grid">
            <div
                v-for="module in sidebarModules"
                :key="module.moduleId"
                class="col"
                :style="columnSettings(module)"
                :class="[verticalAlignment(), isFullBleed(module)]"
            >
                <Component
                    :is="getComponent(module)"
                    :key="module.moduleId"
                    v-bind="getComponentProps(module)"
                />
            </div>
        </div>

        <PortfolioNavigationBar
            v-if="showNavigationBelow"
            :navigation="$store.state.portfolio.portfolioNavigation"
        />
    </div>
</template>

<script>
import SectionBackground from "@/scripts/sections/common/SectionBackground.vue";
import PortfolioNavigationLogo from "@/scripts/sections/common/navigation/PortfolioNavigationLogo.vue";
import PortfolioNavigationBar from "@/scripts/view/portfolio/sections/common/navigation/PortfolioNavigationBar.vue";
import Media from "@/scripts/view/portfolio/sections/modules/Media.vue";
import Text from "@/scripts/view/portfolio/sections/modules/Text.vue";
import FabWrapper from "@/scripts/view/review/components/FabWrapper.vue";
import { SectionModuleTypeEnum } from "@/scripts/common/enums.js";
import { resolveRowSpan } from "@/scripts/common/utilities/layout.js";
import {
    resolveSidebarHeroTextMaxHeight,
    SIDEBAR_HERO_TEXT_MAX_HEIGHT,
} from "@/scripts/common/utilities/sidebarHeroTextHeight.js";
import { mapGetters } from "vuex";
import {updateSectionLevelColorCssInHead} from "@/scripts/common/TextStyleRenderHelper.js";
import {getPortfolioGenerationPreviewPath} from "@/scripts/view/portfolio/generationPreview.js";

export default {
    name: "PortfolioSidebarHeroSection",
    components: {
        PortfolioNavigationBar,
        SectionBackground,
        PortfolioNavigationLogo,
        Media,
        Text,
        FabWrapper,
    },
    props: {
        section: {
            type: Object,
            default: null,
        },
        navigation: {
            type: Object,
            default: null,
        },
        navigationVisibility: {
            type: Object,
            default: () => ({}),
        },
        sidebarHasBackground: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hoverKey: null,
            sidebarHeroTextMaxHeight: SIDEBAR_HERO_TEXT_MAX_HEIGHT,
            sidebarTextResizeObserver: null,
            sidebarTextMaxHeightAnimationFrame: null,
        };
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        sidebarModules() {
            return (this.section?.layoutSettings?.modules || [])
                .filter((module) => {
                    if (module?.type === SectionModuleTypeEnum.MEDIA) return this.section?.contentSettings?.showMedia;
                    if (module?.type === SectionModuleTypeEnum.TEXT) return this.section?.contentSettings?.showText;
                    return false;
                })
                .sort((a, b) => {
                    const aRow = Number(a?.gridSettings?.rowStart) || 0;
                    const bRow = Number(b?.gridSettings?.rowStart) || 0;
                    return aRow - bRow;
                });
        },
        showNavigationAbove() {
            return !!this.navigation && this.navigationVisibility?.showAbove;
        },
        showNavigationBelow() {
            return !!this.navigation && this.navigationVisibility?.showBelow;
        },
        navigationSectionId() {
            return this.navigation?._id ? `section-${this.navigation._id}` : null;
        },
        portfolio() {
            return this.$store.state.portfolio || this.viewData?.portfolio;
        },
        logo() {
            return this.portfolio?.logo || null;
        },
        userName() {
            return this.logo?.name || this.viewData?.user?.name || "";
        },
        showLogo() {
            return !!this.logo?.mode && this.logo.mode !== "None";
        },
        isReviewModeActive() {
            return this.$route.path.includes("/review/");
        },
        contentStyle() {
            const verticalAlign = this.section?.styleSettings?.boxAlign?.split("-")?.[0] || "left";
            const alignMap = {
                left: "flex-start",
                center: "center",
                right: "flex-end",
            };
            return {
                "--heroContentAlign": alignMap[verticalAlign] || "flex-start",
            };
        },
    },
    beforeMount() {
        if(!this.$route.query.templatePreview) {
            updateSectionLevelColorCssInHead(this.section)
        }
    },
    mounted() {
        this.setupSidebarTextMaxHeightObserver();
    },
    beforeUnmount() {
        this.teardownSidebarTextMaxHeightObserver();
    },
    watch: {
        sidebarModules: {
            handler() {
                this.scheduleSidebarTextMaxHeightUpdate();
            },
            deep: true,
        },
        showNavigationAbove() {
            this.scheduleSidebarTextMaxHeightUpdate();
        },
        showNavigationBelow() {
            this.scheduleSidebarTextMaxHeightUpdate();
        },
    },
    methods: {
        getComponent(module) {
            if(module.type === SectionModuleTypeEnum.MEDIA) return "Media";
            if(module.type === SectionModuleTypeEnum.TEXT) return "Text";
            return null;
        },
        getComponentProps(module) {
            const props = { section: this.section };

            if (module.type === SectionModuleTypeEnum.TEXT) {
                props.maxRenderedTextHeight = this.sidebarHeroTextMaxHeight;
            }

            return props;
        },
        calculateMediaAspectRatio(module) {
            if(module.type !== SectionModuleTypeEnum.MEDIA && !this.section.media?.url) return null;

            if(!module.gridSettings?.mediaHeight) return `${this.section.media?.width} / ${this.section.media?.height}`;

            const savedAr = module.gridSettings?.mediaAspectRatio;
            if(savedAr) {
                return `${savedAr[0]} / ${savedAr[1]}`;
            }

            return null;
        },
        columnSettings(module) {
            const { rowStart, rowEnd } = resolveRowSpan(module.gridSettings);
            return {
                "--columnStart": module.gridSettings?.columnStart,
                "--columnEnd": module.gridSettings?.columnEnd,
                "--rowStart": rowStart,
                "--rowEnd": rowEnd,
                "--height": module.gridSettings?.height ? `${module.gridSettings?.height}px` : null,
                "--mediaHeight": !this.calculateMediaAspectRatio(module) && module.gridSettings?.mediaHeight ? `${module.gridSettings?.mediaHeight}px` : null,
                "--mediaAspectRatio": this.calculateMediaAspectRatio(module),
            };
        },
        verticalAlignment() {
            return `aligned-${this.section.styleSettings && this.section.styleSettings?.boxAlign && this.section.styleSettings?.boxAlign.split("-")[1] || "middle"}`;
        },
        isFullBleed(module) {
            return { "is-fullbleed": module.type === SectionModuleTypeEnum.MEDIA && module.gridSettings?.isFullBleed };
        },
        setupSidebarTextMaxHeightObserver() {
            this.teardownSidebarTextMaxHeightObserver();
            if (typeof window === "undefined") return;

            const ResizeObserverRef = window.ResizeObserver || globalThis.ResizeObserver;
            if (ResizeObserverRef) {
                this.sidebarTextResizeObserver = new ResizeObserverRef(() => {
                    this.scheduleSidebarTextMaxHeightUpdate();
                });

                this.$nextTick(() => {
                    const rail = this.getSidebarRailElement();
                    const content = this.$refs.content;
                    if (rail) this.sidebarTextResizeObserver?.observe(rail);
                    if (content) this.sidebarTextResizeObserver?.observe(content);
                });
            }

            window.addEventListener("resize", this.scheduleSidebarTextMaxHeightUpdate);
            this.scheduleSidebarTextMaxHeightUpdate();
        },
        teardownSidebarTextMaxHeightObserver() {
            if (this.sidebarTextResizeObserver) {
                this.sidebarTextResizeObserver.disconnect();
                this.sidebarTextResizeObserver = null;
            }
            if (this.sidebarTextMaxHeightAnimationFrame) {
                cancelAnimationFrame(this.sidebarTextMaxHeightAnimationFrame);
                this.sidebarTextMaxHeightAnimationFrame = null;
            }
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", this.scheduleSidebarTextMaxHeightUpdate);
            }
        },
        scheduleSidebarTextMaxHeightUpdate() {
            if (typeof window === "undefined") return;
            if (this.sidebarTextMaxHeightAnimationFrame) return;

            this.sidebarTextMaxHeightAnimationFrame = requestAnimationFrame(() => {
                this.sidebarTextMaxHeightAnimationFrame = null;
                this.updateSidebarTextMaxHeight();
            });
        },
        updateSidebarTextMaxHeight() {
            this.sidebarHeroTextMaxHeight = resolveSidebarHeroTextMaxHeight({
                rail: this.getSidebarRailElement(),
                textElement: this.getSidebarTextElement(),
            });
        },
        getSidebarRailElement() {
            return this.$refs.content?.closest?.(".sidebar-rail") || null;
        },
        getSidebarTextElement() {
            return this.$refs.content?.querySelector?.(".ufo-sec--hero .col .sidebar-hero-rendered-text") || null;
        },
        rootIsOwnDomain() {
            return this.portfolio?.ownDomainAddress && location.origin.includes(this.portfolio.ownDomainAddress);
        },
        navigateHome() {
            const homeRoute = getPortfolioGenerationPreviewPath(this.$route)
                || (this.rootIsOwnDomain() ? "/" : `/${this.portfolio.shortId}`);
            this.$router.push(homeRoute);
            if (this.viewData) {
                this.viewData.navOpen = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.ufo-sec--hero {
    display: flex;
    flex-direction: column;
    gap: 24px;
    height: 100%;
    justify-content: var(--heroContentAlign, flex-start);
    min-height: 0;
    position: relative;
    width: 100%;
    z-index: 1;
}

.sidebar-logo-section {
    display: flex;
}
</style>
